import {
  Box,
  Button,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import { tLabel, tMessage, useForm, UseFormProps, useTranslation } from '@/core'
/**
 * @description
 * pickupTimeの配列を受け取り、編集するフォーム
 */
import { CellFrame, Organism } from '@/core/architecture'
import { DateTime } from '@/core/entities'

import { MaskedInputCell, TextInputCell } from './common'
import { PickupTimeModal, PickupTimeModalProps } from './PickupTimeModal'

export type PickupTime = {
  pickupDate: string
  pickupTime: {
    options: {
      time: string
      availableUserNumber: number
      currentUserNumber: number
    }
  }[]
}

export type PickupTimeError = {
  pickupTime: {
    options: {
      time: string
    }
  }[]
}

type PickupTimesEditFormProps = {
  pickupTimes: PickupTime[]
  setPickupTimes: Dispatch<SetStateAction<PickupTime[]>>
  days: string[]
  pickupTimeErrors: PickupTimeError[]
}

export const PickupTimesEditForm = ({
  pickupTimes,
  setPickupTimes,
  days,
  pickupTimeErrors,
}: PickupTimesEditFormProps) => {
  const { t } = useTranslation('consoleEventCollectionReceiveTimeEdit')
  const [open, setOpen] = useState(false)

  /**
   * モーダルのフォームを決定したときに受取時間stateを更新する処理日付が一致するものに対して、時間を追加する
   * 入力値から得られた情報を元のステートにマージする
   */
  const handleSubmitPickupTimeModal = (
    values: Parameters<PickupTimeModalProps['onSubmit']>[0]
  ) => {
    if (!values.pickupTime.length) return

    setPickupTimes(state => {
      return state.map(node => {
        if (!values.pickupDate.includes(node.pickupDate)) return node

        return {
          ...node,
          pickupTime: [
            ...node.pickupTime,
            ...values.pickupTime.map(({ options }, i) => ({
              options: {
                time: options.time,
                availableUserNumber: options.availableUserNumber,
                currentUserNumber:
                  node.pickupTime.find(
                    ({ options: _opt }) => _opt.time === options.time
                  )?.options.currentUserNumber ?? 0,
              },
            })),
          ]
            // 重複を除外
            .reduceRight((acc, cur) => {
              if (!acc.some(e => e.options.time === cur.options.time)) {
                acc.unshift(cur)
              }
              return acc
            }, [] as { options: { time: string; availableUserNumber: number; currentUserNumber: number } }[])
            // 時間が若い順にソート
            .sort((a, b) => (a.options.time > b.options.time ? 1 : -1)),
        }
      })
    })
  }

  return (
    <CellFrame>
      <Typography variant="h5">
        {t('pickupvalidMinutes', '受け渡し開始時間と対応可能人数')}
      </Typography>
      <Typography variant="body2" sx={{ pt: 1, color: 'text.secondary' }}>
        {t(
          'pickupvalidMinutesNote',
          '対応可能人数を入力すると、購入者数を制限できます。受け渡しの有効時間内に対応可能な人数を入力してください。'
        )}
      </Typography>
      <Typography variant="caption" sx={{ pt: 1, color: 'text.secondary' }}>
        {t(
          'pickupvalidMinutesCaution',
          '※「受け渡し開始時間」は、ユーザーが商品を購入するまでに限り、編集・削除する事ができます。'
        )}
      </Typography>
      <Stack>
        <Button
          variant="outlined"
          size="medium"
          onClick={() => setOpen(true)}
          sx={{ borderColor: 'primary.main', color: 'primary.main', mt: 3 }}
        >
          {t('selectDate', '日付を選んで一括設定する')}
        </Button>
        {/* モーダルで受取時間stateを編集するフォーム */}
        <PickupTimeModal
          open={open}
          onClose={() => setOpen(false)}
          days={days}
          onSubmit={handleSubmitPickupTimeModal}
        />
      </Stack>
      {/* ページ内で受取時間stateを編集するフォーム群 */}
      <PickupTimeForm
        pickupTimes={pickupTimes}
        setPickupTimes={setPickupTimes}
        pickupTimeErrors={pickupTimeErrors}
      />
    </CellFrame>
  )
}

/**
 * @description
 * ページ内で受取時間stateを編集するフォーム群
 * stateの日付ごとに時間・受け渡し可能人数を入力するフォームをグルーピングして表示する
 */
const PickupTimeForm = ({
  pickupTimes,
  setPickupTimes,
  pickupTimeErrors,
}: {
  pickupTimes: PickupTime[]
  setPickupTimes: Dispatch<SetStateAction<PickupTime[]>>
  pickupTimeErrors: PickupTimeError[]
}) => {
  const { t } = useTranslation('consoleEventCollectionReceiveTimeEdit')
  return (
    <Stack>
      <TableContainer sx={{ py: 4 }}>
        <Table>
          <TableHead
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <TableRow
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <TableCell
                padding="normal"
                sx={{
                  width: 'auto',
                  pl: 0,
                  pr: 2,
                  borderBottom: 'none',
                  pb: 0,
                }}
              >
                <Typography variant="subtitle1">
                  {t('deliveryStartTime')}
                </Typography>
              </TableCell>
              <TableCell
                padding="normal"
                sx={{
                  flex: 1,
                  pl: 0,
                  pr: 2,
                  borderBottom: 'none',
                  pb: 0,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    whiteSpace: 'nowrap',
                    textAlign: 'right',
                    position: 'relative',
                  }}
                >
                  {t('currentUserNumber')} {t('availablePeople')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* 入力フォームを日付単位でループ表示する */}
            {pickupTimes.map((pickupTime, index) => {
              return (
                <PickupTimeCell
                  key={index}
                  pickupTime={pickupTime}
                  setPickupTimes={setPickupTimes}
                  pickupTimeError={pickupTimeErrors[index]?.pickupTime}
                />
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

/**
 * 日付ごとの時間指定フォームを表示するコンポーネント
 */
const PickupTimeCell = ({
  pickupTime,
  setPickupTimes,
  pickupTimeError,
}: {
  pickupTime: PickupTime
  setPickupTimes: Dispatch<SetStateAction<PickupTime[]>>
  pickupTimeError?: { options: { time: string } }[]
}) => {
  const pickup = pickupTime.pickupTime

  // ゴミ箱アイコンを押した時の処理。該当する時間をstateから除外する
  const handleRemove = (index: number) => {
    setPickupTimes(state => {
      return state.map(node => {
        if (node.pickupDate !== pickupTime.pickupDate) return node

        return {
          ...node,
          pickupTime: node.pickupTime.filter((_, i) => i !== index),
        }
      })
    })
  }

  // 日付横の+ボタンを押した時の処理。stateの日付に対応する時間に空の値を追加する
  const handlePlus = () => {
    setPickupTimes(state => {
      return state.map(node => {
        if (node.pickupDate !== pickupTime.pickupDate) return node

        return {
          ...node,
          pickupTime: [
            ...node.pickupTime,
            {
              options: {
                time: '',
                availableUserNumber: 0,
                currentUserNumber: 0,
              },
            },
          ],
        }
      })
    })
  }

  // ソートと重複除外の処理、個々のフォーム側で入力が終わったときに実行する
  const handleSort = () => {
    setPickupTimes(state => {
      return state.map(node => {
        if (node.pickupDate !== pickupTime.pickupDate) return node

        return {
          ...node,
          pickupTime: node.pickupTime
            // 重複を除外
            .reduceRight((acc, cur) => {
              if (!acc.some(e => e.options.time === cur.options.time)) {
                acc.unshift(cur)
              }
              return acc
            }, [] as { options: { time: string; availableUserNumber: number; currentUserNumber: number } }[])
            // ソート
            .sort((a, b) =>
              !b.options.time
                ? -1
                : !a.options.time || a.options.time > b.options.time
                ? 1
                : -1
            ),
        }
      })
    })
  }

  // 個々のフォームについて、時間を更新する。
  const handleChangeTime = (input: string, index: number) => {
    setPickupTimes(state => {
      return state.map(node => {
        if (node.pickupDate !== pickupTime.pickupDate) return node

        return {
          ...node,
          pickupTime: node.pickupTime.map((time, i) => {
            if (i !== index) return time

            return {
              ...time,
              options: {
                ...time.options,
                time: input,
              },
            }
          }),
        }
      })
    })
  }

  // 個々のフォームについて、受け渡し可能人数を更新する
  const handleChangeAvailableUserNumber = (input: number, index: number) => {
    setPickupTimes(state => {
      return state.map(node => {
        if (node.pickupDate !== pickupTime.pickupDate) return node

        return {
          ...node,
          pickupTime: node.pickupTime.map((time, i) => {
            if (i !== index) return time

            return {
              ...time,
              options: {
                ...time.options,
                availableUserNumber: input,
              },
            }
          }),
        }
      })
    })
  }

  return (
    <>
      <TableRow>
        <TableCell
          colSpan={3}
          sx={{
            borderBottom: 'none',
            pb: 0,
            px: 0,
            pt: 4,
          }}
        >
          <Divider sx={{ color: 'grey.100' }} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 4 }}>
            <Typography variant="subtitle1">
              {DateTime.formatDate(pickupTime.pickupDate, 'YYYY/MM/DD (ddd)')}
            </Typography>
            <IconButton.Plus
              bgcolor="primary.main"
              sx={{
                bgcolor: 'primary.main',
                width: 20,
                height: 20,
                '&:hover': {
                  bgcolor: 'primary.main',
                  opacity: 0.8,
                },
                svg: {
                  fontSize: '12px',
                  color: 'white',
                },
              }}
              size="small"
              disabled={pickup.length >= 20}
              onClick={handlePlus}
            />
          </Stack>
        </TableCell>
      </TableRow>

      {pickup.map(({ options }, index) => {
        return (
          <PickupRowTime
            key={index}
            index={index}
            options={options}
            onRemove={handleRemove}
            onBlurTime={handleChangeTime}
            onBlurAvailableUserNumber={handleChangeAvailableUserNumber}
            onSort={handleSort}
            errorTime={pickupTimeError?.[index].options.time}
          />
        )
      })}
    </>
  )
}

type PickupRowTimeProps = {
  index: number
  options: {
    time: string
    availableUserNumber: number
    currentUserNumber: number
  }
  onRemove: (index: number) => void
  onBlurTime: (input: string, index: number) => void
  onBlurAvailableUserNumber: (input: number, index: number) => void
  onSort: () => void
  errorTime?: string
}

/**
 * 受取時間変更フォームの1つ1つのフォームコンポーネント
 * それぞれがreact-hook-formのスキーマを持ち、個別にバリデーションチェックを行う
 */
const PickupRowTime: Organism<PickupRowTimeProps> = ({
  options,
  onRemove,
  index,
  onBlurTime,
  onBlurAvailableUserNumber,
  onSort,
  errorTime,
}) => {
  // 1度blurを行ったかどうか。これ以降にバリデーションチェックを行うようにする
  const [slotted, setSlotted] = useState(false)

  const schema: UseFormProps['schema'] = o => ({
    time: o
      .string()
      .regex(/^([01][0-9]|2[0-3]):([0-5][0-9])$/)
      .messages({
        'string.empty': tMessage(
          'consoleEventCollectionReceiveTimeEdit.pickupTime.time.string.empty'
        ),
      })
      .label(tLabel('consoleEventCollectionReceiveTimeEdit.pickupTime.time')),
    availableUserNumber: o
      .number()
      .min(options.currentUserNumber ?? 1)
      .label(
        tLabel(
          'consoleEventCollectionReceiveTimeEdit.pickupTime.availableUserNumber'
        )
      ),
  })

  const {
    formState: { errors },
    register,
    reset,
    watch,
  } = useForm<PickupRowTimeProps['options']>({
    schema: schema,
    defaultValues: {
      time: options.time ?? '',
      availableUserNumber: options.availableUserNumber ?? 0,
    },
  })

  useEffect(() => {
    reset({
      time: options.time ?? '',
      availableUserNumber: options.availableUserNumber ?? 0,
    })
  }, [options])

  return (
    <TableRow
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
        fieldset: {
          border: options.currentUserNumber ? 'none' : 'solid 1px lightgray',
        },
      }}
    >
      <TableCell
        padding="normal"
        sx={{
          borderBottom: 'none',
          pl: 0,
          pr: 0,
          pb: 0,
          position: 'relative',
          div: {
            xs: {
              minWidth: '72px',
            },
            '@media(min-width:450px)': {
              minWidth: 'auto',
              flex: 1,
            },
          },
          'p.Mui-error': {
            height: 0,
            position: 'absolute',
            bottom: 0,
            background: 'transparent',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <MaskedInputCell
          {...register('time')}
          type="time"
          mask="00:00"
          textFieldProps={{
            error:
              (slotted &&
                !options.currentUserNumber &&
                (!!errors?.time ||
                  (!options.currentUserNumber && !watch('time')) ||
                  !watch('time')?.match(
                    /^([01][0-9]|2[0-3]):([0-5][0-9])$/
                  ))) ||
              !!errorTime,
            helperText:
              (slotted &&
                !options.currentUserNumber &&
                (errors.time?.message ||
                  errors.availableUserNumber?.message ||
                  (!watch('time') &&
                    tMessage(
                      'consoleEventCollectionReceiveTimeEdit.pickupTime.time.string.empty'
                    )) ||
                  (!watch('time')?.match(/^([01][0-9]|2[0-3]):([0-5][0-9])$/) &&
                    tMessage(
                      'consoleEventCollectionReceiveTimeEdit.pickupTime.time.string.pattern'
                    )) ||
                  (!options.currentUserNumber &&
                    !watch('availableUserNumber') &&
                    tMessage(
                      'consoleEventCollectionReceiveTimeEdit.pickupTime.availableUserNumber.number.empty'
                    )) ||
                  (watch('availableUserNumber') <= 0 &&
                    tMessage(
                      'consoleEventCollectionReceiveTimeEdit.pickupTime.availableUserNumber.number.invalid'
                    )))) ||
              errorTime,
            FormHelperTextProps: { error: !!errors || !!errorTime, sx: {} },
            onBlur: e => {
              setSlotted(true)
              onBlurTime(e.target.value, index)

              if (
                !Object.keys(errors).length &&
                !!watch('availableUserNumber')
              ) {
                onSort()
              }
            },
            disabled: !!options.currentUserNumber,
          }}
        />
      </TableCell>
      <TableCell
        padding="normal"
        sx={{
          width: 'auto',
          borderBottom: 'none',
          pl: 0,
          pr: 0,
          pb: 0,
          div: {
            minWidth: options.currentUserNumber ? '40px' : 'inherit',
          },
          'p.Mui-error': {
            height: 0,
            position: 'absolute',
            bottom: 0,
            background: 'transparent',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={3}>
          <Box>
            <Typography sx={{ width: 56 }} align="right">
              {options.availableUserNumber &&
              Number(options.currentUserNumber) !== 0
                ? options.currentUserNumber
                : '-'}{' '}
              /
            </Typography>
          </Box>

          <Box sx={{ '.MuiFormHelperText-root': { display: 'none' } }}>
            <TextInputCell
              {...register('availableUserNumber')}
              type="tel"
              titleProps={{ display: 'none' }}
              textFieldProps={{
                sx: {
                  my: 0,
                  width: 100,
                },
                error:
                  !!errors?.availableUserNumber ||
                  (slotted &&
                    !options.currentUserNumber &&
                    (!watch('availableUserNumber') ||
                      watch('availableUserNumber') <= 0)),
                FormHelperTextProps: { error: !!errors },
                placeholder: '200',
                onBlur: e => {
                  setSlotted(true)
                  onBlurAvailableUserNumber(Number(e.target.value), index)
                  if (
                    !Object.keys(errors).length &&
                    !!watch('availableUserNumber')
                  ) {
                    onSort()
                  }
                },
              }}
            />
          </Box>
          <IconButton.Trash
            size="small"
            disabled={!!options.currentUserNumber}
            onClick={() => onRemove(index)}
          />
        </Stack>
      </TableCell>
    </TableRow>
  )
}
